import { useUpdate } from 'ahooks';
import { isObject } from 'lodash';
import { useEffect, useRef } from 'react';
import { type Editor } from '@meitu/saas-editor-core';
import { LayerType, type ILayerProps } from '@meitu/saas-editor-core/es/version-types';
import { findFirstTextLayer } from '../utils';

const checkIsChanged = <T>(prev: T, next: T) => {
    if (prev === null && next === null) return false;
    if (prev === null || next === null) return true;
    if (isObject(prev) && isObject(next)) {
        return JSON.stringify(prev) !== JSON.stringify(next);
    }
    return prev !== next;
};

export const useLayerProps = <T>(editor: Editor, pickFn: (layer: ILayerProps | null) => T) => {
    const forceUpdate = useUpdate();

    const maybeGroupLayer = editor.services.frame.getActivePickLayer(true);

    const pickLayer = (() => {
        if (!maybeGroupLayer) return null;

        if (maybeGroupLayer.layerType === LayerType.GROUP_LAYER) {
            return findFirstTextLayer(maybeGroupLayer);
        }
        return maybeGroupLayer;
    })();

    const propsRef = useRef<T>(pickFn(pickLayer));

    useEffect(() => {
        const handler = editor.services.frame.onActiveLayerDataUpdate((activeLayer, pickLayer) => {
            const layer = pickLayer ? pickLayer : findFirstTextLayer(activeLayer);
            const props = pickFn(layer);

            const isChanged = checkIsChanged(propsRef.current, props);
            if (isChanged) {
                propsRef.current = props;
                forceUpdate();
            }
        });
        return () => {
            editor.off(handler);
        };
    }, []);

    return propsRef.current;
};
